/**
 * Central app configuration — SINGLE SOURCE OF TRUTH for which backend the
 * web app talks to.
 *
 * Flip ONE variable to switch the whole app between the dev and production
 * servers:
 *   USE_DEV_SERVER = true   -> dev backend   (https://dev-api.kjuup.de)
 *   USE_DEV_SERVER = false  -> prod backend  (https://app.kjuup.de)
 *
 * This is intentionally kept in code (committed) instead of `.env` so the
 * server target is explicit and consistent across every build/machine.
 * Secrets (Google keys, etc.) still live in the backend `.env`.
 *
 * IMPORTANT: USE_LOCAL_SERVER MUST be false when committing / shipping builds.
 *
 * NOTE: CommonJS `module.exports` so this file can be consumed by both the
 * React app (webpack `import`) and `src/setupProxy.js` (Node `require`).
 */

// ⚠️ LOCAL TESTING ONLY — keep false when committing.
const USE_LOCAL_SERVER = false;
const USE_DEV_SERVER = true;
// Legacy compile-time Stripe switch — kept for reference only.
// Runtime visibility is controlled via Admin Panel:
//   Settings → Stripe / Kjuup.io (global, all Kjuup users)
//   User details → Kjuup.io (single Kjuup user)
//   Company → Stripe / Kjuup.io (per Kjuup+ company)
// FE gates use profile/subscription.stripeFeatureEnabled (+ company flag).
const test_stripe = false;
// Calendar Sync in Settings — UI is shown when Stripe/Kjuup.io is enabled for
// the user (runtime flag via profile/subscription). Not tied to which backend
// the app talks to.
const calendarFeatureEnabled = true;

const SERVERS = {
  local: {
    API_URL: "http://localhost:1333",
    SOCKET_URL: "http://localhost:1333",
    // Admin Panel (`npm run dev` defaults to PORT=3001).
    ADMIN_PANEL_URL: "http://localhost:3001",
  },
  dev: {
    API_URL: "https://dev-api.kjuup.de",
    SOCKET_URL: "https://dev-api.kjuup.de",
    ADMIN_PANEL_URL: "http://localhost:3001",
  },
  prod: {
    // Production endpoint — update once the new prod server is live.
    API_URL: "https://app.kjuup.de",
    SOCKET_URL: "https://app.kjuup.de",
    // Update when the production Admin Panel host is final.
    ADMIN_PANEL_URL: "https://admin.kjuup.de",
  },
};

const ACTIVE = USE_LOCAL_SERVER
  ? SERVERS.local
  : USE_DEV_SERVER
    ? SERVERS.dev
    : SERVERS.prod;

// ─── Cockpit / product sidebar links ────────────────────────────────────────
// Kjuup+ is booked via the request form on the website ("Anfrage senden").
const KJUUP_PLUS_REQUEST_URL = "https://kjuup.de/pakete";
// Same page with the Kjuup+ request form already open.
const KJUUP_PLUS_ANFRAGE_URL = `${KJUUP_PLUS_REQUEST_URL}?anfrage=kjuup-plus`;
// Same page with the Kjuup.io request form open (Kjuup.io not enabled for the user yet).
const KJUUP_IO_ANFRAGE_URL = `${KJUUP_PLUS_REQUEST_URL}?anfrage=kjuup-io`;
// Kjuup.io is booked in-app (Stripe) from the personal settings page.
const KJUUP_IO_BOOKING_ROUTE = "/profile-setting";
// "Weiterempfehlen" — link shared with colleagues / partners.
const REFERRAL_URL = "https://kjuup.de";
// Future products listed in the sidebar. Empty string = not available yet
// (renders a "coming soon" teaser instead of a link).
const PRODUCT_URLS = {
  josch: "",
  projektview: "",
  kostenprognose: "",
  terminplan: "",
};

module.exports = {
  USE_LOCAL_SERVER,
  USE_DEV_SERVER,
  test_stripe,
  calendarFeatureEnabled,
  SERVERS,
  API_URL: ACTIVE.API_URL,
  SOCKET_URL: ACTIVE.SOCKET_URL,
  ADMIN_PANEL_URL: ACTIVE.ADMIN_PANEL_URL,
  KJUUP_PLUS_REQUEST_URL,
  KJUUP_PLUS_ANFRAGE_URL,
  KJUUP_IO_ANFRAGE_URL,
  KJUUP_IO_BOOKING_ROUTE,
  REFERRAL_URL,
  PRODUCT_URLS,
};
